import Papa from "papaparse";
import { supabase } from "./expenseService";

const clean = (value) => String(value || "").trim();

const parseMoney = (value) => {
  const raw = clean(value).replace(/[$,]/g, "").replace(/[()]/g, (match) => (match === "(" ? "-" : ""));
  const number = Number(raw);
  return Number.isFinite(number) ? number : 0;
};

const findColumn = (row, candidates) => {
  const keys = Object.keys(row || {});
  const match = keys.find((key) => candidates.some((candidate) => key.toLowerCase().includes(candidate)));
  return match ? row[match] : "";
};

const normalizeDate = (value, fallback = "") => {
  const text = clean(value);
  if (!text) return fallback;
  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  return text.slice(0, 10);
};

export function parseAirbnbCsv(file, properties = []) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data, errors }) => {
        if (errors?.length) {
          reject(new Error(errors[0].message));
          return;
        }
        const rows = (data || []).map((row, index) => {
          const listingName = clean(findColumn(row, ["listing", "property", "home", "room"]));
          const gross = parseMoney(findColumn(row, ["gross", "amount", "earnings", "income", "price"]));
          const fees = Math.abs(parseMoney(findColumn(row, ["fee", "service", "host fee"]))) || 0;
          const payout = parseMoney(findColumn(row, ["payout", "paid", "net"]));
          const matched = properties.find((property) => {
            const haystack = `${property.name || ""} ${property.nickname || ""}`.toLowerCase();
            return listingName && (haystack.includes(listingName.toLowerCase()) || listingName.toLowerCase().includes((property.name || "").toLowerCase()));
          });
          return {
            client_id: `${Date.now()}-${index}`,
            source: "Airbnb CSV",
            source_file_name: file.name,
            reservation_id: clean(findColumn(row, ["reservation", "confirmation", "booking"])),
            guest_name: clean(findColumn(row, ["guest"])),
            listing_name: listingName,
            income_date: normalizeDate(findColumn(row, ["date", "start", "payout date", "booking date"]), new Date().toISOString().slice(0, 10)),
            check_in_date: normalizeDate(findColumn(row, ["check-in", "check in", "start date"])),
            check_out_date: normalizeDate(findColumn(row, ["check-out", "check out", "end date"])),
            gross_amount: gross || payout,
            host_fee_amount: fees,
            cleaning_fee_amount: parseMoney(findColumn(row, ["cleaning"])),
            payout_amount: payout || Math.max(gross - fees, 0),
            currency: clean(findColumn(row, ["currency"])) || "USD",
            property_id: matched?.id || "",
            status: matched ? "Matched" : "Needs Review",
            notes: "Imported from Airbnb CSV",
          };
        });
        resolve(rows.filter((row) => row.gross_amount || row.payout_amount || row.reservation_id || row.listing_name));
      },
      error: (error) => reject(error),
    });
  });
}

export async function saveIncomeRows(userId, rows) {
  const payload = rows.map((row) => ({
    user_id: userId,
    property_id: row.property_id || null,
    source: row.source || "Airbnb CSV",
    source_file_name: row.source_file_name || "",
    reservation_id: row.reservation_id || null,
    guest_name: row.guest_name || "",
    listing_name: row.listing_name || "",
    income_date: row.income_date,
    check_in_date: row.check_in_date || null,
    check_out_date: row.check_out_date || null,
    gross_amount: Number(row.gross_amount || 0),
    host_fee_amount: Number(row.host_fee_amount || 0),
    cleaning_fee_amount: Number(row.cleaning_fee_amount || 0),
    payout_amount: Number(row.payout_amount || 0),
    currency: row.currency || "USD",
    status: row.status || (row.property_id ? "Matched" : "Needs Review"),
    notes: row.notes || "Imported from Airbnb CSV",
  }));
  const { data, error } = await supabase.from("income_transactions").insert(payload).select("*");
  if (error) throw error;
  return data || [];
}

export function summarizeIncome(rows = []) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.toISOString().slice(0, 7);
  const inYear = rows.filter((row) => new Date(`${row.income_date}T00:00:00`).getFullYear() === currentYear);
  const inMonth = rows.filter((row) => String(row.income_date || "").slice(0, 7) === currentMonth);
  const total = (items, field) => items.reduce((acc, row) => acc + Number(row[field] || 0), 0);
  return {
    monthGross: total(inMonth, "gross_amount"),
    monthPayout: total(inMonth, "payout_amount"),
    yearGross: total(inYear, "gross_amount"),
    yearPayout: total(inYear, "payout_amount"),
    needsReview: rows.filter((row) => row.status !== "Matched" || !row.property_id).length,
  };
}